import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { UsersService } from './users.service';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ModuleModel } from '../models/module.model';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ModuleService {
  private readonly baseUrl: string;
  
  
  constructor(
    private firebaseService: FirebaseService,
    private userService: UsersService,
    private http: HttpClient,
  ) {
    this.baseUrl = `${environment.apiUrl}`;
  }
  
  public geModulePercentages = async (key: string, companyId: string): Promise<any> => {
    const [userModule, module] = await Promise.all([
      this.firebaseService.getDocument(`companies/${companyId}/settings/modules/${key}/percentages`),
      await lastValueFrom(
        this.getModuleByKey(key).pipe(
          map(m => {
              const result: Record<string, number> = {};
              for (const section in m?.sections) {
                result[section] = m.sections[section]?.percentage ?? 0;
              }
              return result;
            }
          )
        )
      )
    ]);
    return {
      ...(module || {}),
      ...userModule
    };
  };
  
  
  public getStatusModuleSections = async (module: string, uid: string): Promise<any> => {
    const user = await this.userService.getUser(uid);
    const [avatar, bestPractices, introduction, peer] = await Promise.all([
      this.getStatusModuleSection(module, 'avatar', user.companyId),
      this.getStatusModuleSection(module, 'bestPractices', user.companyId),
      this.getStatusModuleSection(module, 'introduction', user.companyId),
      this.getStatusModuleSection(module, 'peer', user.companyId),
    ]);
    return {
      avatar,
      bestPractices,
      introduction,
      peer
    };
  };
  
  public getStatusModuleSection = async (module: string, section: string, companyId: string): Promise<boolean> => {
    let result: boolean = await this.firebaseService.getDocument(`companies/${companyId}/settings/modules/${module}/${section}`);
    if (result === null) {
      result = await this.firebaseService.getDocument(`settings/modules/${module}/${section}`);
    }
    return result || false;
  };
  
  public getModuleByKey = (module: string): Observable<any> =>
    this.http.get<any>(`${this.baseUrl}/modules/key/${module}`);
  
  public getModules = (): Observable<ModuleModel[]> =>
    this.http.get<ModuleModel[]>(`${this.baseUrl}/modules`);
  
  public updateModule = (id: string, payload: ModuleModel): Observable<void> =>
    this.http.put<void>(`${this.baseUrl}/modules/${encodeURIComponent(id)}`, payload);
  
  public insertModule = (payload: ModuleModel): Observable<string> =>
    this.http.post(`${this.baseUrl}/modules`, payload, {responseType: 'text'});
  
  public getModulePropertyByKey = (key: string, property: string): Observable<any> =>
    this.http.get<any>(`${this.baseUrl}/key/${key}/property`, { params: { property } });
  
  public deleteModule = (id: string): Promise<void> =>
    this.http.delete<void>(`${this.baseUrl}/modules/${encodeURIComponent(id)}`).toPromise();
  
  public getCompanyModules = async (companyId: string): Promise<any[]> => {
    const modules: string[] = await this.firebaseService.getDocument(`companies/${companyId}/settings/availableModules`);
    if (modules.length > 0) {
      const modulesReq = [];
      for (const module of modules) {
        modulesReq.push(this.getCompanyModule(companyId, module));
      }
      return await Promise.all(modulesReq);
    }
    const modulesData = await lastValueFrom(this.getModules());
    const settingsModules = await this.firebaseService.getDocumentsListOnce(`settings/modules`);
    const result: any[] = [];
    for (const sm of settingsModules) {
      const md = modulesData.find((m: any) => m.key === sm.key);
      result.push({
        ...sm,
        title: md?.title,
        description: md?.description
      });
    }
    return result;
    
  };
  
  public getCompanyModule = async (companyId: string, key: string): Promise<any> => {
    const [userModule, module] = await Promise.all([
      this.firebaseService.getDocument(`companies/${companyId}/settings/modules/${key}`),
      lastValueFrom(this.getModuleByKey(key))
    ]);
    return {
      ...(module || {}),
      ...userModule,
      title: module?.title,
      description: module?.description,
    };
  };
  
  public async getCompanyModulesKeys(companyId: string): Promise<string[]> {
    const companyModules = await this.getCompanyModules(companyId);
    return companyModules.map(m => m.key);
  }
}
